import React, { useState } from 'react';
import Button from './ui/Button';
import Textarea from './ui/Textarea';
import { Input } from './ui';

const LapseAcknowledgmentFlow = ({ userId, currentDay, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0); // 0: intro, 1: what happened, 2: immediate response, 3: moving forward, 4: resources, 5: confirmation
  const [isSaving, setIsSaving] = useState(false);

  const [lapseData, setLapseData] = useState({
    lapseDate: new Date().toISOString().split('T')[0],
    substanceBehavior: '',
    context: {
      location: '',
      emotionalState: '',
      triggers: '',
      peoplePresent: 'alone'
    },
    immediateResponse: {
      stoppedQuickly: false,
      usedCopingSkills: false,
      reachedOutForHelp: false,
      copingSkillsUsed: '',
      notes: ''
    },
    commitment: {
      whatWillDoDifferently: '',
      supportNeeded: '',
      nextSteps: ''
    },
    resourcesAccessed: [],
    clinicianNotified: false
  });

  const handleChange = (section, field, value) => {
    if (section) {
      setLapseData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setLapseData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const toggleResource = (resource) => {
    setLapseData(prev => ({
      ...prev,
      resourcesAccessed: prev.resourcesAccessed.includes(resource)
        ? prev.resourcesAccessed.filter(r => r !== resource)
        : [...prev.resourcesAccessed, resource]
    }));
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    try {
      // Process the data
      const payload = {
        lapseDate: lapseData.lapseDate,
        substanceBehavior: lapseData.substanceBehavior,
        context: {
          ...lapseData.context,
          triggers: lapseData.context.triggers
            .split(',')
            .map(t => t.trim())
            .filter(Boolean)
        },
        immediateResponse: {
          ...lapseData.immediateResponse,
          copingSkillsUsed: lapseData.immediateResponse.copingSkillsUsed
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        },
        commitment: {
          ...lapseData.commitment,
          nextSteps: lapseData.commitment.nextSteps
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        },
        resourcesAccessed: lapseData.resourcesAccessed,
        clinicianNotified: lapseData.clinicianNotified,
        dayNumberAtLapse: currentDay
      };

      await onComplete(payload);
    } catch (error) {
      console.error('Failed to save setback report:', error);
      alert('Failed to save setback report. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const isStep1Valid = () => {
    return lapseData.substanceBehavior.trim().length > 0;
  };

  const isStep3Valid = () => {
    return lapseData.commitment.whatWillDoDifferently.trim().length > 0;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white p-6 rounded-t-lg">
          <h2 className="text-2xl font-bold">
            You're Still Here. That Matters.
          </h2>
          <p className="mt-2 text-teal-50">
            Progress isn't a straight line. Let's acknowledge what happened and move forward together.
          </p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-1 py-4 px-6 bg-gray-50">
          {[0, 1, 2, 3, 4, 5].map((step) => (
            <div key={step} className={`h-2 flex-1 rounded-full transition-colors ${
              step <= currentStep ? 'bg-teal-500' : 'bg-gray-300'
            }`} />
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 0: Introduction */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">
                First: Take a Breath
              </h3>
              <p className="text-gray-700 leading-relaxed">
                You're taking a courageous step by acknowledging what happened. This isn't about judgment.
                It's about understanding, learning, and moving forward.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <h4 className="font-semibold text-blue-900 mb-2">What This Is:</h4>
                <ul className="list-disc list-inside space-y-1 text-blue-800 text-sm">
                  <li>A safe space to process what happened</li>
                  <li>A tool to identify patterns and stressors</li>
                  <li>A pathway back to your mental wellness journey</li>
                  <li>Private unless you choose to share with your clinician</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <h4 className="font-semibold text-yellow-900 mb-2">What This Is Not:</h4>
                <ul className="list-disc list-inside space-y-1 text-yellow-800 text-sm">
                  <li>A reset of your progress (your days count stays)</li>
                  <li>A failure or mark against you</li>
                  <li>Shared with anyone without your permission</li>
                  <li>Something to be ashamed of</li>
                </ul>
              </div>

              <div className="mt-6 p-4 bg-gradient-to-r from-teal-50 to-blue-50 rounded">
                <p className="text-gray-800 font-medium italic">
                  "Mental wellness is not about perfection. It's about direction. One setback doesn't erase
                  your progress—it's information that helps you move forward stronger."
                </p>
              </div>
            </div>
          )}

          {/* Step 1: What Happened */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">What Happened</h3>
              <p className="text-gray-700">
                Let's start with the basics. You don't have to share every detail—just enough to help you understand.
              </p>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  When did this happen? *
                </label>
                <Input
                  type="date"
                  value={lapseData.lapseDate}
                  onChange={(e) => handleChange(null, 'lapseDate', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What behavior or pattern occurred? *
                </label>
                <Input
                  value={lapseData.substanceBehavior}
                  onChange={(e) => handleChange(null, 'substanceBehavior', e.target.value)}
                  placeholder="e.g., Missed self-care routine, unhealthy coping, isolation, negative thought spiral..."
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Where were you?
                </label>
                <Input
                  value={lapseData.context.location}
                  onChange={(e) => handleChange('context', 'location', e.target.value)}
                  placeholder="e.g., At home, at work, out with friends..."
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Who was present?
                </label>
                <select
                  value={lapseData.context.peoplePresent}
                  onChange={(e) => handleChange('context', 'peoplePresent', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                >
                  <option value="alone">Alone</option>
                  <option value="friends">Friends</option>
                  <option value="family">Family</option>
                  <option value="partner">Partner</option>
                  <option value="strangers">Strangers</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What were you feeling before it happened?
                </label>
                <Textarea
                  value={lapseData.context.emotionalState}
                  onChange={(e) => handleChange('context', 'emotionalState', e.target.value)}
                  placeholder="Anxious, lonely, stressed, overwhelmed, frustrated, exhausted..."
                  rows={3}
                  maxLength={500}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What stressors contributed? (separate with commas)
                </label>
                <Input
                  value={lapseData.context.triggers}
                  onChange={(e) => handleChange('context', 'triggers', e.target.value)}
                  placeholder="Conflict, work stress, poor sleep, skipped meals, financial worry..."
                />
              </div>
            </div>
          )}

          {/* Step 2: Immediate Response */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">What You Did Next</h3>
              <p className="text-gray-700">
                Your response matters. Even small protective actions are worth noting.
              </p>

              <div className="space-y-3">
                <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={lapseData.immediateResponse.stoppedQuickly}
                    onChange={(e) => handleChange('immediateResponse', 'stoppedQuickly', e.target.checked)}
                    className="w-5 h-5 text-teal-600"
                  />
                  <span className="text-gray-800">I recognized it and redirected quickly</span>
                </label>

                <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={lapseData.immediateResponse.usedCopingSkills}
                    onChange={(e) => handleChange('immediateResponse', 'usedCopingSkills', e.target.checked)}
                    className="w-5 h-5 text-teal-600"
                  />
                  <span className="text-gray-800">I used coping skills before or after</span>
                </label>

                <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={lapseData.immediateResponse.reachedOutForHelp}
                    onChange={(e) => handleChange('immediateResponse', 'reachedOutForHelp', e.target.checked)}
                    className="w-5 h-5 text-teal-600"
                  />
                  <span className="text-gray-800">I reached out to someone for support</span>
                </label>
              </div>

              {lapseData.immediateResponse.usedCopingSkills && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    What coping skills did you use? (separate with commas)
                  </label>
                  <Input
                    value={lapseData.immediateResponse.copingSkillsUsed}
                    onChange={(e) => handleChange('immediateResponse', 'copingSkillsUsed', e.target.value)}
                    placeholder="Deep breathing, called a friend, went for a walk, journaled, mindfulness exercise..."
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Anything else you want to note?
                </label>
                <Textarea
                  value={lapseData.immediateResponse.notes}
                  onChange={(e) => handleChange('immediateResponse', 'notes', e.target.value)}
                  placeholder="How you felt afterward, what you learned, what surprised you..."
                  rows={4}
                  maxLength={1000}
                />
              </div>
            </div>
          )}

          {/* Step 3: Moving Forward */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">Moving Forward</h3>
              <p className="text-gray-700">
                This is where growth happens—identifying what you'll do differently and what support you need.
              </p>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What will you do differently next time you face this situation? *
                </label>
                <Textarea
                  value={lapseData.commitment.whatWillDoDifferently}
                  onChange={(e) => handleChange('commitment', 'whatWillDoDifferently', e.target.value)}
                  placeholder="Be specific: 'I'll use a grounding technique when I feel overwhelmed' or 'I'll reach out to my support person before the stress builds up'..."
                  rows={4}
                  maxLength={500}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What support do you need?
                </label>
                <Textarea
                  value={lapseData.commitment.supportNeeded}
                  onChange={(e) => handleChange('commitment', 'supportNeeded', e.target.value)}
                  placeholder="More check-ins, different coping tools, accountability partner, therapy session..."
                  rows={3}
                  maxLength={500}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What are your next steps? (separate with commas)
                </label>
                <Input
                  value={lapseData.commitment.nextSteps}
                  onChange={(e) => handleChange('commitment', 'nextSteps', e.target.value)}
                  placeholder="Update safety plan, schedule therapy session, practice mindfulness daily, talk to support person..."
                />
              </div>
            </div>
          )}

          {/* Step 4: Resources */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">Support Resources</h3>
              <p className="text-gray-700 mb-4">
                These resources are here for you right now. Check any you want to access.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { id: 'safety_plan', label: 'View My Safety Plan', icon: '🛡️' },
                  { id: 'crisis_line', label: '988 Crisis Line', icon: '☎️' },
                  { id: 'grounding_exercises', label: 'Grounding Exercises', icon: '🧘' },
                  { id: 'distress_tolerance', label: 'Distress Tolerance Skills', icon: '💪' },
                  { id: 'journaling', label: 'Journal About This', icon: '📝' },
                  { id: 'counselor', label: 'Contact My Counselor', icon: '👤' },
                  { id: 'support_group', label: 'Support Group Info', icon: '👥' },
                  { id: 'emergency_contact', label: 'Call Emergency Contact', icon: '🆘' }
                ].map((resource) => (
                  <label
                    key={resource.id}
                    className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      lapseData.resourcesAccessed.includes(resource.id)
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={lapseData.resourcesAccessed.includes(resource.id)}
                      onChange={() => toggleResource(resource.id)}
                      className="w-5 h-5 text-teal-600"
                    />
                    <span className="text-2xl">{resource.icon}</span>
                    <span className="text-gray-800 font-medium">{resource.label}</span>
                  </label>
                ))}
              </div>

              <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                <p className="text-red-900 font-semibold mb-2">If you're in immediate crisis:</p>
                <ul className="text-red-800 text-sm space-y-1">
                  <li>• Call 988 (Suicide & Crisis Lifeline)</li>
                  <li>• Text "HELLO" to 741741 (Crisis Text Line)</li>
                  <li>• Call 911 or go to the nearest emergency room</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === 5 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">Almost Done</h3>
              <p className="text-gray-700">
                One more choice: would you like to share this with your clinician?
              </p>

              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lapseData.clinicianNotified}
                    onChange={(e) => handleChange(null, 'clinicianNotified', e.target.checked)}
                    className="w-5 h-5 text-teal-600 mt-1"
                  />
                  <div>
                    <span className="text-gray-900 font-medium block">
                      Yes, notify my clinician
                    </span>
                    <span className="text-gray-600 text-sm">
                      They'll be able to see this report and offer additional support.
                    </span>
                  </div>
                </label>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mt-6">
                <h4 className="font-semibold text-blue-900 mb-2">What Happens Next:</h4>
                <ul className="list-disc list-inside space-y-1 text-blue-800 text-sm">
                  <li>Your report is saved privately</li>
                  <li>Your program progress continues from where you are</li>
                  <li>You'll return to your daily content</li>
                  <li>Your clinician will be notified if you checked that option</li>
                  <li>You can review this report anytime in your progress section</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-4 rounded mt-6">
                <p className="text-gray-800 font-medium text-center">
                  Mental wellness is about showing up again after you stumble. You're doing that right now.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex items-center justify-between border-t">
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button onClick={handleBack} variant="secondary" disabled={isSaving}>
                ← Back
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            {currentStep === 0 && (
              <>
                <Button onClick={onClose} variant="ghost">
                  Not Right Now
                </Button>
                <Button onClick={handleNext}>
                  Continue →
                </Button>
              </>
            )}

            {currentStep > 0 && currentStep < 4 && (
              <Button
                onClick={handleNext}
                disabled={
                  (currentStep === 1 && !isStep1Valid()) ||
                  (currentStep === 3 && !isStep3Valid())
                }
              >
                Next →
              </Button>
            )}

            {currentStep === 4 && (
              <Button onClick={handleNext}>
                Next →
              </Button>
            )}

            {currentStep === 5 && (
              <Button onClick={handleSubmit} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Submit & Continue'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LapseAcknowledgmentFlow;
